/*
 * File: EmailAccountInput.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 14, 2025 at 12:06 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { maskEmail } from '../../../utilities/utilities';

// Components
import { TextInput } from '../TextInput';
import { Button } from '../Button';
import { Typography } from '../Typography';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './EmailAccountInput.styles';


/**
 * Component
 */

export const EmailAccountInput = ({
	className, email, placeholder, handleOnChange, isLocked, unlockField
}) => {

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Create obfuscated email
	const obfuscatedEmail = useMemo(() => (email ? maskEmail(email) : null), [email]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className}>

			{/* Locker */}
			{isLocked && (
				<S.Locker>
					<Typography tag="p">{obfuscatedEmail}</Typography>
					<Button variant="text" onClick={unlockField}>
						<Typography variation="button-small">
							<Trans t={t} i18nKey="emailAccountInput.notYouButton">
								Not you?
							</Trans>
						</Typography>
					</Button>
				</S.Locker>
			)}

			{/* Email Input */}
			<TextInput
				placeholder={placeholder}
				name="email"
				type="text"
				value={email || ''}
				onChange={handleOnChange}
				disabled={isLocked}
			/>

		</S.Wrapper>
	);
};


/**
 * Configuration
 */

EmailAccountInput.displayName = 'EmailAccountInput';
EmailAccountInput.propTypes = {
	className: PropTypes.string,
	email: PropTypes.string,
	placeholder: PropTypes.string,
	handleOnChange: PropTypes.func,
	isLocked: PropTypes.bool,
	unlockField: PropTypes.func
};
EmailAccountInput.defaultProps = {
	className: null,
	email: null,
	placeholder: null,
	handleOnChange: null,
	isLocked: false,
	unlockField: null
};
